import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getAuthHeader } from "../../helpers/authHelper";
import SponsorshipOptionModel from "../../models/SponsorshipOptionModel";
import Loading from "../shared/Loading";
import Swal from "sweetalert2";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { sanitizeHtml } from "../../helpers/htmlHelper";

const fetchSponsorshipOptions = async (): Promise<SponsorshipOptionModel[]> => {
    const response = await fetch("/api/sponsorship/options");
    if (!response.ok) {
        throw new Error("Failed to fetch sponsorship options");
    }
    return response.json();
};

const SponsorshipAdmin = () => {
    const queryClient = useQueryClient();

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [amount, setAmount] = useState<number | "">("");
    const [limit, setLimit] = useState<number | "">("");
    const [isUnlimited, setIsUnlimited] = useState(false);
    const [sortOrder, setSortOrder] = useState<number | "">(0);

    // Edit state
    const [editingOption, setEditingOption] = useState<SponsorshipOptionModel | null>(null);
    const [editTitle, setEditTitle] = useState("");
    const [editDescription, setEditDescription] = useState("");
    const [editAmount, setEditAmount] = useState<number | "">("");
    const [editLimit, setEditLimit] = useState<number | "">("");
    const [editIsUnlimited, setEditIsUnlimited] = useState(false);
    const [editSortOrder, setEditSortOrder] = useState<number | "">(0);

    const {
        data: options,
        isLoading,
        error,
    } = useQuery<SponsorshipOptionModel[], Error>({
        queryKey: ["sponsorshipOptions"],
        queryFn: fetchSponsorshipOptions,
    });

    const createMutation = useMutation({
        mutationFn: async (newOption: { title: string; description: string; amount: number; limit: number; sortOrder: number }) => {
            const response = await fetch("/api/sponsorship/options", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: getAuthHeader(),
                },
                body: JSON.stringify(newOption),
            });
            if (!response.ok) {
                throw new Error("Failed to create sponsorship option");
            }
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["sponsorshipOptions"] });
            setTitle("");
            setDescription("");
            setAmount("");
            setLimit("");
            setIsUnlimited(false);
            setSortOrder(0);
            Swal.fire({
                icon: "success",
                title: "Created",
                text: "Sponsorship option created successfully!",
                timer: 2000,
                showConfirmButton: false,
            });
        },
        onError: () => {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to create sponsorship option.",
            });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: number) => {
            const response = await fetch(`/api/sponsorship/options/${id}`, {
                method: "DELETE",
                headers: {
                    Authorization: getAuthHeader(),
                },
            });
            if (!response.ok) {
                throw new Error("Failed to delete sponsorship option");
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["sponsorshipOptions"] });
        },
    });

    const handleCreate = () => {
        if (!isFormValid) return;
        createMutation.mutate({
            title,
            description,
            amount: Number(amount),
            limit: isUnlimited ? 0 : Number(limit),
            sortOrder: Number(sortOrder) || 0,
        });
    };

    const handleDelete = (id: number, optionTitle: string) => {
        Swal.fire({
            title: "Are you sure?",
            text: `This will deactivate "${optionTitle}".`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, remove it",
        }).then((result) => {
            if (result.isConfirmed) {
                deleteMutation.mutate(id);
            }
        });
    };

    const isFormValid = title.trim().length > 0 && amount !== "" && Number(amount) > 0 && (isUnlimited || (limit !== "" && Number(limit) > 0));

    const editMutation = useMutation({
        mutationFn: async (data: { id: number; title: string; description: string; amount: number; limit: number; sortOrder: number }) => {
            const response = await fetch(`/api/sponsorship/options/${data.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: getAuthHeader(),
                },
                body: JSON.stringify({
                    title: data.title,
                    description: data.description,
                    amount: data.amount,
                    limit: data.limit,
                    sortOrder: data.sortOrder,
                }),
            });
            if (!response.ok) {
                throw new Error("Failed to update sponsorship option");
            }
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["sponsorshipOptions"] });
            setEditingOption(null);
            Swal.fire({
                icon: "success",
                title: "Updated",
                text: "Sponsorship option updated successfully!",
                timer: 2000,
                showConfirmButton: false,
            });
        },
        onError: () => {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to update sponsorship option.",
            });
        },
    });

    const handleEdit = (option: SponsorshipOptionModel) => {
        setEditingOption(option);
        setEditTitle(option.title);
        setEditDescription(option.description || "");
        setEditAmount(option.amount);
        setEditLimit(option.limit === 0 ? "" : option.limit);
        setEditIsUnlimited(option.limit === 0);
        setEditSortOrder(option.sortOrder);
    };

    const handleSaveEdit = () => {
        if (!editingOption) return;
        editMutation.mutate({
            id: editingOption.sponsorshipOptionId,
            title: editTitle,
            description: editDescription,
            amount: Number(editAmount),
            limit: editIsUnlimited ? 0 : Number(editLimit),
            sortOrder: Number(editSortOrder) || 0,
        });
    };

    const isEditFormValid = editTitle.trim().length > 0 && editAmount !== "" && Number(editAmount) > 0 && (editIsUnlimited || (editLimit !== "" && Number(editLimit) > 0));

    const quillModules = {
        toolbar: [
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['bold', 'italic', 'underline'],
            ['clean']
        ]
    };

    if (isLoading) {
        return <Loading message="Loading Sponsorship Options..." />;
    }

    if (error) {
        return (
            <div className="text-center mt-5 text-danger">
                {error.message || "Failed to fetch sponsorship options"}
            </div>
        );
    }

    return (
        <div className="container pt-4">
            <div className="row align-items-center">
                <div className="col-8">
                    <h2 className="text-dark">Manage Sponsorship Options</h2>
                </div>
                <div className="col-4 d-flex justify-content-end">
                    <Link to="/" className="btn btn-primary d-flex align-items-center">
                        <i className="bi bi-house me-2"></i> Home
                    </Link>
                </div>
                <div className="col-12">
                    <hr />
                </div>
            </div>

            {/* Create Form */}
            <div className="card mb-4">
                <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">Create New Sponsorship Option</h5>
                </div>
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-6">
                            <div className="form-floating">
                                <input
                                    className="form-control"
                                    id="title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Title"
                                />
                                <label htmlFor="title">Title</label>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-floating">
                                <input
                                    className="form-control"
                                    id="amount"
                                    type="number"
                                    min="1"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value === "" ? "" : Number(e.target.value))}
                                    placeholder="Amount"
                                />
                                <label htmlFor="amount">Amount ($)</label>
                            </div>
                        </div>
                        <div className="col-md-3">
                            <div className="form-check form-switch mb-2 mt-2">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="unlimitedToggle"
                                    checked={isUnlimited}
                                    onChange={(e) => {
                                        setIsUnlimited(e.target.checked);
                                        if (e.target.checked) setLimit("");
                                    }}
                                />
                                <label className="form-check-label" htmlFor="unlimitedToggle">Unlimited</label>
                            </div>
                            {!isUnlimited && (
                                <div className="form-floating">
                                    <input
                                        className="form-control"
                                        id="limit"
                                        type="number"
                                        min="1"
                                        value={limit}
                                        onChange={(e) => setLimit(e.target.value === "" ? "" : Number(e.target.value))}
                                        placeholder="Limit"
                                    />
                                    <label htmlFor="limit">Limit (Available Spots)</label>
                                </div>
                            )}
                        </div>
                        <div className="col-md-2">
                            <div className="form-floating">
                                <input
                                    className="form-control"
                                    id="sortOrder"
                                    type="number"
                                    min="0"
                                    value={sortOrder}
                                    onChange={(e) => setSortOrder(e.target.value === "" ? "" : Number(e.target.value))}
                                    placeholder="Sort Order"
                                />
                                <label htmlFor="sortOrder">Sort Order</label>
                            </div>
                        </div>
                        <div className="col-12">
                            <label className="form-label">Description (Optional)</label>
                            <ReactQuill
                                theme="snow"
                                value={description}
                                onChange={setDescription}
                                placeholder="Enter a description..."
                                modules={quillModules}
                            />
                        </div>
                        <div className="col-12">
                            <button
                                className="btn btn-success w-100 py-2"
                                disabled={!isFormValid || createMutation.isPending}
                                onClick={handleCreate}
                            >
                                {createMutation.isPending ? "Creating..." : "Create Sponsorship Option"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Existing Options */}
            <h4 className="mb-3">Current Sponsorship Options</h4>
            {(!options || options.length === 0) && (
                <div className="alert alert-info">No sponsorship options have been created yet.</div>
            )}
            <div className="row">
                {options?.map((option) => (
                    <div key={option.sponsorshipOptionId} className="col-lg-4 col-md-6 col-sm-12 mb-3">
                        <div className="card h-100">
                            <div className="card-body d-flex flex-column">
                                <h5 className="card-title">{option.title}</h5>
                                {option.description && (
                                    <div className="card-text text-muted rich-text-content" dangerouslySetInnerHTML={{ __html: sanitizeHtml(option.description) }} />
                                )}
                                <div className="mt-auto">
                                    <p className="mb-1">
                                        <strong>Sort Order:</strong> {option.sortOrder}
                                    </p>
                                    <p className="mb-1">
                                        <strong>Amount:</strong> ${option.amount.toFixed(2)}
                                    </p>
                                    <p className="mb-1">
                                        <strong>Claimed:</strong> {option.claimedCount} / {option.limit === 0 ? "Unlimited" : option.limit}
                                    </p>
                                    {option.isSoldOut && (
                                        <span className="badge bg-danger">Sold Out</span>
                                    )}
                                </div>
                            </div>
                            <div className="card-footer d-flex gap-2">
                                <button
                                    className="btn btn-outline-primary btn-sm flex-grow-1"
                                    onClick={() => handleEdit(option)}
                                >
                                    Edit
                                </button>
                                <button
                                    className="btn btn-outline-danger btn-sm flex-grow-1"
                                    onClick={() => handleDelete(option.sponsorshipOptionId, option.title)}
                                    disabled={deleteMutation.isPending}
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Edit Modal */}
            {editingOption && (
                <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title">Edit Sponsorship Option</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setEditingOption(null)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <div className="form-floating">
                                            <input
                                                className="form-control"
                                                id="editTitle"
                                                value={editTitle}
                                                onChange={(e) => setEditTitle(e.target.value)}
                                                placeholder="Title"
                                            />
                                            <label htmlFor="editTitle">Title</label>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="form-floating">
                                            <input
                                                className="form-control"
                                                id="editAmount"
                                                type="number"
                                                min="1"
                                                value={editAmount}
                                                onChange={(e) => setEditAmount(e.target.value === "" ? "" : Number(e.target.value))}
                                                placeholder="Amount"
                                            />
                                            <label htmlFor="editAmount">Amount ($)</label>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="form-check form-switch mb-2 mt-2">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="editUnlimitedToggle"
                                                checked={editIsUnlimited}
                                                onChange={(e) => {
                                                    setEditIsUnlimited(e.target.checked);
                                                    if (e.target.checked) setEditLimit("");
                                                }}
                                            />
                                            <label className="form-check-label" htmlFor="editUnlimitedToggle">Unlimited</label>
                                        </div>
                                        {!editIsUnlimited && (
                                            <div className="form-floating">
                                                <input
                                                    className="form-control"
                                                    id="editLimit"
                                                    type="number"
                                                    min="1"
                                                    value={editLimit}
                                                    onChange={(e) => setEditLimit(e.target.value === "" ? "" : Number(e.target.value))}
                                                    placeholder="Limit"
                                                />
                                                <label htmlFor="editLimit">Limit (Available Spots)</label>
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-md-3">
                                        <div className="form-floating">
                                            <input
                                                className="form-control"
                                                id="editSortOrder"
                                                type="number"
                                                min="0"
                                                value={editSortOrder}
                                                onChange={(e) => setEditSortOrder(e.target.value === "" ? "" : Number(e.target.value))}
                                                placeholder="Sort Order"
                                            />
                                            <label htmlFor="editSortOrder">Sort Order</label>
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label">Description (Optional)</label>
                                        <ReactQuill
                                            theme="snow"
                                            value={editDescription}
                                            onChange={setEditDescription}
                                            placeholder="Enter a description..."
                                            modules={quillModules}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setEditingOption(null)}>Cancel</button>
                                <button
                                    className="btn btn-primary"
                                    disabled={!isEditFormValid || editMutation.isPending}
                                    onClick={handleSaveEdit}
                                >
                                    {editMutation.isPending ? "Saving..." : "Save Changes"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SponsorshipAdmin;
